<script>
import Vue from "vue";

// "Connaissez-vous la Saisie rapide ?" is shown once per user per browser. Every switch on the page
// shares this state, so the copy hidden by the responsive classes never shows it a second time.
const TIP_KEY_PREFIX = "amiral.quickOrderTip.seen.";
const tipState = Vue.observable({ key: null, seen: true, open: false });

// Each mode shows its own copy of the switch (catalogue header, Saisie rapide header). A copy that appears
// starts its highlight where the previous one was and slides from there, so both directions animate.
let lastThumbRight = null;

function readSeen(key) {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch (e) {
    return true; // storage blocked: never nag
  }
}

function writeSeen(key) {
  try {
    window.localStorage.setItem(key, "1");
  } catch (e) {
    // storage blocked: the tip just stays closed for this page
  }
}

/**
 * Catalogue | Saisie rapide switch of the order page, used with v-model="isQuickOrder".
 */
export default {
  name: "QuickOrderSwitch",
  props: {
    value: { type: Boolean, default: false },
    // "Tapez le nom de l'article…" under the switch while quick order is active
    hint: { type: Boolean, default: false },
    // phones: both options share the full width
    block: { type: Boolean, default: false },
    // one-time bubble pointing at "Saisie rapide"
    tip: { type: Boolean, default: false },
  },
  data() {
    return { tipTimer: null, thumbRight: lastThumbRight === null ? this.value : lastThumbRight };
  },
  computed: {
    // the tip only mentions the CSV import where it is enabled (VUE_APP_FEATURE_QUICK_ORDER_CSV_IMPORT)
    csvImportEnabled() {
      return process.env.VUE_APP_FEATURE_QUICK_ORDER_CSV_IMPORT === 'true';
    },
    showTip() {
      return this.tip && tipState.open && !this.value;
    },
    tipKey() {
      return TIP_KEY_PREFIX + ((this.$cookies && this.$cookies.get("authUser")) || "anonymous");
    },
  },
  watch: {
    value(isQuickOrder) {
      if (isQuickOrder) this.dismissTip();
      this.thumbRight = isQuickOrder;
      lastThumbRight = isQuickOrder;
    },
  },
  mounted() {
    if (this.thumbRight !== this.value) {
      // two frames so the browser paints the start position before sliding
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => { this.thumbRight = this.value; }));
    }
    lastThumbRight = this.value;
    if (!this.tip) return;
    if (tipState.key !== this.tipKey) {
      tipState.key = this.tipKey;
      tipState.seen = readSeen(this.tipKey);
      tipState.open = false;
    }
    if (tipState.seen || this.value) return;
    this.tipTimer = setTimeout(() => {
      if (!tipState.seen && !this.value) tipState.open = true;
    }, 800);
  },
  beforeDestroy() {
    clearTimeout(this.tipTimer);
  },
  methods: {
    tryQuickOrder() {
      this.dismissTip();
      this.$emit("input", true);
    },
    dismissTip() {
      tipState.open = false;
      if (!tipState.seen) {
        tipState.seen = true;
        writeSeen(this.tipKey);
      }
    },
  },
};
</script>

<template>
  <div class="qo" :class="{ 'qo--block': block }">
    <div class="qo-switch" role="tablist" aria-label="Mode de saisie de la commande">
      <span class="qo-switch__thumb" :class="{ 'qo-switch__thumb--right': thumbRight }" aria-hidden="true"></span>
      <button type="button" role="tab" class="qo-switch__option" :class="{ 'qo-switch__option--active': !value }" :aria-selected="!value ? 'true' : 'false'" @click="$emit('input', false)">
        <i class="bx bx-grid-alt" aria-hidden="true"></i><span>Catalogue</span>
      </button>
      <button type="button" role="tab" class="qo-switch__option" :class="{ 'qo-switch__option--active': value, 'qo-switch__option--tip': showTip }" :aria-selected="value ? 'true' : 'false'" @click="$emit('input', true)">
        <i class="bx bxs-bolt" aria-hidden="true"></i><span>Saisie rapide</span>
      </button>
    </div>

    <div v-if="showTip" class="qo-tip" role="dialog" aria-label="Saisie rapide">
      <span class="qo-tip__arrow" aria-hidden="true"></span>
      <p class="qo-tip__title">Connaissez-vous la Saisie rapide ?</p>
      <p class="qo-tip__text">Saisissez votre commande en ligne, article par article<template v-if="csvImportEnabled">, ou importez une commande complète à partir d'un fichier CSV</template>.</p>
      <div class="qo-tip__actions">
        <button type="button" class="qo-tip__later" @click="dismissTip">Plus tard</button>
        <button type="button" class="qo-tip__try" @click="tryQuickOrder"><i class="bx bxs-bolt" aria-hidden="true"></i>Essayer</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qo { position: relative; display: inline-block; max-width: 100%; text-align: left; }
.qo-switch { position: relative; display: inline-grid; grid-template-columns: 1fr 1fr; padding: .3rem; background: #f2f4f7; border-radius: 12px; }
/* white highlight that slides to the active option (both options share the same width) */
.qo-switch__thumb { position: absolute; top: .3rem; bottom: .3rem; left: .3rem; width: calc(50% - .3rem); border-radius: 9px; background: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, .08), 0 1px 3px rgba(16, 24, 40, .1); transition: transform .45s cubic-bezier(.22, 1, .36, 1), box-shadow .45s ease; will-change: transform; }
.qo-switch__thumb--right { transform: translateX(100%); }
.qo-switch__option { position: relative; z-index: 1; display: inline-flex; align-items: center; justify-content: center; gap: .45rem; padding: .5rem 1.1rem; border: 0; border-radius: 9px; background: transparent; color: #475467; font-size: 14px; font-weight: 600; white-space: nowrap; cursor: pointer; transition: color .3s ease; }
.qo-switch__option .bx { font-size: 18px; color: #667085; transition: color .3s ease, transform .45s cubic-bezier(.22, 1, .36, 1); }
.qo-switch__option:hover { color: #101828; }
.qo-switch__option--active { color: #101828; background: transparent; }
.qo-switch__option--active .bx { color: #101828; transform: scale(1.06); }
.qo-switch__option:focus-visible { outline: 2px solid #344054; outline-offset: 2px; }
.qo-switch__hint { margin: .45rem 0 0; font-size: 12.5px; color: #667085; text-align: right; }
.qo--block { display: block; }
.qo--block .qo-switch { display: grid; }
.qo--block .qo-switch__hint { text-align: left; }

/* one-time bubble under the switch, arrow on "Saisie rapide"; same vivid orange as the delivery "waiting" state */
.qo-tip { position: absolute; top: calc(100% + 12px); right: 0; z-index: 30; width: 300px; max-width: calc(100vw - 32px); padding: .9rem 1rem .8rem; background: #fff7ed; border: 1px solid #fdba74; border-radius: 12px; box-shadow: 0 12px 24px -6px rgba(194, 65, 12, .22), 0 4px 8px -2px rgba(16, 24, 40, .08); text-align: left; animation: qo-tip-in .2s ease-out; }
.qo-tip__arrow { position: absolute; top: -7px; right: 22%; width: 12px; height: 12px; background: #fff7ed; border-top: 1px solid #fdba74; border-left: 1px solid #fdba74; border-radius: 2px 0 0 0; transform: rotate(45deg); }
.qo-tip__title { margin: 0 0 .25rem; font-size: 14px; font-weight: 700; color: #9a3412; }
.qo-tip__text { margin: 0; font-size: 13px; line-height: 1.45; color: #475467; }
.qo-tip__actions { display: flex; align-items: center; justify-content: flex-end; gap: .5rem; margin-top: .75rem; }
.qo-tip__later { padding: .35rem .5rem; border: 0; border-radius: 6px; background: transparent; color: #667085; font-size: 13px; font-weight: 600; cursor: pointer; }
.qo-tip__later:hover { color: #101828; }
.qo-tip__try { display: inline-flex; align-items: center; gap: .35rem; padding: .4rem .85rem; border: 0; border-radius: 8px; background: #c2410c; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; }
.qo-tip__try:hover { background: #9a3412; }
.qo-tip__try .bx { font-size: 15px; }
.qo-tip__later:focus-visible, .qo-tip__try:focus-visible { outline: 2px solid #c2410c; outline-offset: 2px; }
.qo--block .qo-tip { left: 0; right: 0; width: auto; max-width: none; }
.qo-switch__option--tip { color: #c2410c; }
.qo-switch__option--tip .bx { color: #c2410c; animation: qo-pulse 1.6s ease-in-out infinite; }

@keyframes qo-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.25); } }
@keyframes qo-tip-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }

@media (prefers-reduced-motion: reduce) {
  .qo-switch__option, .qo-switch__thumb, .qo-switch__option .bx { transition: none; }
  .qo-switch__option--active .bx { transform: none; }
  .qo-tip, .qo-switch__option--tip .bx { animation: none; }
}
</style>
